/* eslint-disable react/require-default-props, react/no-unused-prop-types */
import React, { useMemo, useState, useCallback } from 'react';
import { BaseFilter } from '../BaseFilter';
import { Popup } from '../../Popup';
import './TreeSelectFilterStyles.scss';
function flattenOptions(options) {
    var _a;
    const flattened = [];
    const stack = [...options];
    while (stack.length > 0) {
        const current = stack.shift();
        if (!current)
            continue;
        flattened.push(current);
        if ((_a = current.items) === null || _a === void 0 ? void 0 : _a.length) {
            stack.unshift(...current.items);
        }
    }
    return flattened;
}
function filterTree(options, query) {
    if (query.trim() === '')
        return options;
    const normalizedQuery = query.trim().toLowerCase();
    return options
        .map((option) => {
        var _a;
        const parentMatch = option.label.toLowerCase().includes(normalizedQuery);
        const children = (_a = option.items) !== null && _a !== void 0 ? _a : [];
        const matchingChildren = children.filter((child) => child.label.toLowerCase().includes(normalizedQuery));
        if (parentMatch)
            return option;
        if (matchingChildren.length > 0)
            return Object.assign(Object.assign({}, option), { items: matchingChildren });
        return null;
    })
        .filter((option) => option != null);
}
export function TreeSelectFilter({ label, value, onChange, options, placeholder = 'Search', disabled = false, showReset = true }) {
    const [search, setSearch] = useState('');
    const [expandedValues, setExpandedValues] = useState([]);
    const flatOptions = useMemo(() => flattenOptions(options), [options]);
    const selectedLabel = useMemo(() => { var _a; return (value == null ? undefined : (_a = flatOptions.find((option) => option.value === value)) === null || _a === void 0 ? void 0 : _a.label); }, [flatOptions, value]);
    const filteredOptions = useMemo(() => filterTree(options, search), [options, search]);
    const handleReset = useCallback(() => onChange(null), [onChange]);
    const toggleExpanded = useCallback((nodeValue) => {
        setExpandedValues((current) => current.includes(nodeValue)
            ? current.filter((expandedValue) => expandedValue !== nodeValue)
            : [...current, nodeValue]);
    }, []);
    const renderOptionButton = (option, close) => {
        var _a, _b;
        const isSelected = value === option.value;
        const hasChildren = Boolean((_a = option.items) === null || _a === void 0 ? void 0 : _a.length);
        const isExpanded = Boolean(search.trim()) || expandedValues.includes(option.value);
        return (React.createElement("li", { key: String(option.value), className: "qmb-ui-tree-select-filter__row" },
            React.createElement("div", { className: "qmb-ui-tree-select-filter__item" },
                React.createElement("button", { type: "button", className: "qmb-ui-tree-select-filter__option", "aria-pressed": isSelected, onClick: () => {
                        onChange(option.value);
                        close();
                    } }, option.label),
                hasChildren && (React.createElement("button", { type: "button", className: "qmb-ui-tree-select-filter__expand", "aria-label": `Toggle ${option.label}`, "aria-expanded": isExpanded, "aria-controls": `qmb-ui-tree-select-filter-children-${String(option.value)}`, onClick: () => toggleExpanded(option.value) },
                    React.createElement("i", { className: `fa-light ${isExpanded ? 'fa-angle-up' : 'fa-angle-down'}`, "aria-hidden": "true" })))),
            hasChildren && isExpanded && (React.createElement("ul", { id: `qmb-ui-tree-select-filter-children-${String(option.value)}`, className: "qmb-ui-tree-select-filter__children", role: "presentation" }, (_b = option.items) === null || _b === void 0 ? void 0 : _b.map((child) => (React.createElement("li", { key: String(child.value) },
                React.createElement("button", { type: "button", className: "qmb-ui-tree-select-filter__child-option", "aria-pressed": value === child.value, onClick: () => {
                        onChange(child.value);
                        close();
                    } }, child.label))))))));
    };
    return (React.createElement(BaseFilter, { label: label, isActive: value != null, selectedDisplay: selectedLabel, disabled: disabled, onReset: showReset && value != null ? handleReset : undefined }, (close) => (React.createElement("div", { className: "qmb-ui-tree-select-filter" },
        React.createElement(Popup.SearchInput, { value: search, placeholder: placeholder, onChange: setSearch }),
        React.createElement(Popup.Section, { fillHeight: true },
            React.createElement("ul", { className: "qmb-ui-tree-select-filter__list", role: "presentation" },
                filteredOptions.length === 0 && React.createElement("li", { className: "qmb-ui-tree-select-filter__empty" }, "No results"),
                filteredOptions.map((option) => renderOptionButton(option, close))))))));
}
